import DOMPurify from 'dompurify';
import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function Messagerie() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchPhone, setSearchPhone] = useState('');
  const [searching, setSearching] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [sendingImages, setSendingImages] = useState(false);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioStreamRef = useRef(null);
  const messageInputRef = useRef(null);
  const formatMenuRef = useRef(null);

  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const normalizeChatMessage = (msg) => ({
    ...msg,
    url_fichier: msg?.url_fichier
      ? config.normalizeMessageMedia(msg.url_fichier)
      : null,
    photo_profil: msg?.photo_profil
      ? config.normalizeProfilePhoto(msg.photo_profil)
      : null,
    expediteur_photo: msg?.expediteur_photo
      ? config.normalizeProfilePhoto(msg.expediteur_photo)
      : null
  });

  const MAX_DURATION = 240;
  const MAX_SIZE = 40 * 1024 * 1024;

  const applyFormat = (before, after = before) => {
    if (!messageInputRef.current) return;
    const input = messageInputRef.current;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const selectedText = newMessage.substring(start, end);
    let newText;
    if (selectedText) {
      newText = newMessage.substring(0, start) + before + selectedText + after + newMessage.substring(end);
    } else {
      newText = newMessage + before + after;
    }
    setNewMessage(newText);
    setShowFormatMenu(false);
    setTimeout(() => {
      if (selectedText) {
        input.selectionStart = start + before.length;
        input.selectionEnd = end + before.length;
      } else {
        const newPos = newText.length - after.length;
        input.selectionStart = newPos;
        input.selectionEnd = newPos;
      }
      input.focus();
    }, 10);
  };

  const formatMessageContent = (content) => {
    if (!content) return '';
    return content
      .replace(/`([^`]+)`/g, '<code class="bg-black/10 dark:bg-white/10 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-bold">$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>')
      .replace(/~~([^~]+)~~/g, '<span class="line-through">$1</span>')
      .replace(/^# (.+)$/gm, '<h1 class="text-base font-extrabold my-1">$1</h1>')
      .replace(/^## (.+)$/gm, '<h2 class="text-sm font-bold my-1">$1</h2>')
      .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
      .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')
      .replace(/\n/g, '<br/>');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formatMenuRef.current && !formatMenuRef.current.contains(event.target)) {
        setShowFormatMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchConversations = async () => {
    try {
      const token = getToken();
      if (!token) { setLoading(false); return; }
      const response = await fetch(`${API_BASE}/messagerie/get_conversations.php`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        const convs = (data.conversations || []).map(conv => ({
          ...conv,
          correspondant_photo: config.normalizeProfilePhoto(conv.correspondant_photo)
        }));
        setConversations(convs);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (id_conversation) => {
    try {
      const token = getToken();
      if (!token) return;
      const response = await fetch(`${API_BASE}/messagerie/get_messages.php?id=${id_conversation}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        const msgs = (data.messages || []).map(normalizeChatMessage);
        setMessages(msgs);
        scrollToBottom();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const sendTextMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;
    setSending(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/messagerie/send_message.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ id_destinataire: selectedUser.id_utilisateur, contenu: newMessage, type_message: 'texte' })
      });
      const data = await response.json();
      if (data.success) {
        setMessages(prev => [...prev, normalizeChatMessage(data.message)]);
        setNewMessage('');
        scrollToBottom();
        fetchConversations();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur d\'envoi');
    } finally {
      setSending(false);
    }
  };

  const sendMultipleImages = async () => {
    if (!selectedUser || selectedImages.length === 0) return;
    setSendingImages(true);
    let successCount = 0;
    for (const file of selectedImages) {
      if (file.size > MAX_SIZE) { toast.error(`${file.name} > 40 Mo, ignoré`); continue; }
      const reader = new FileReader();
      await new Promise((resolve) => {
        reader.onloadend = async () => {
          try {
            const token = getToken();
            const response = await fetch(`${API_BASE}/messagerie/send_message.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({
                id_destinataire: selectedUser.id_utilisateur,
                contenu: '',
                type_message: 'image',
                base64_data: reader.result,
                file_name: file.name
              })
            });
            const data = await response.json();
            if (data.success) { setMessages(prev => [...prev, normalizeChatMessage(data.message)]); successCount++; }
          } catch (err) { console.error(err); }
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }
    if (successCount > 0) { toast.success(`${successCount} image(s) envoyée(s)`); scrollToBottom(); fetchConversations(); }
    setSendingImages(false);
    setSelectedImages([]);
  };

  const sendFile = async (file, type) => {
    if (!selectedUser) return;
    if (file.size > MAX_SIZE) { toast.error(`Fichier trop volumineux (max 40 Mo)`); return; }
    setSending(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const token = getToken();
        const response = await fetch(`${API_BASE}/messagerie/send_message.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            id_destinataire: selectedUser.id_utilisateur,
            contenu: '',
            type_message: type,
            base64_data: reader.result,
            file_name: file.name
          })
        });
        const data = await response.json();
        if (data.success) {
          setMessages(prev => [...prev, normalizeChatMessage(data.message)]);
          scrollToBottom();
          fetchConversations();
          toast.success('Fichier envoyé');
        }
      } catch (error) { toast.error('Erreur d\'envoi'); }
      finally { setSending(false); }
    };
    reader.readAsDataURL(file);
  };

  const deleteMessage = async () => {
    if (!messageToDelete) return;
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/messagerie/delete_message.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ id_message: messageToDelete.id_message })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Message supprimé');
        setMessages(prev => prev.filter(m => m.id_message !== messageToDelete.id_message));
        fetchConversations();
      } else { toast.error(data.message); }
    } catch (error) { toast.error('Erreur'); }
    finally { setShowDeleteModal(false); setMessageToDelete(null); }
  };

  const handleImageSelection = (e) => {
    const files = Array.from(e.target.files);
    const images = files.filter(f => f.type.startsWith('image/'));
    if (images.length === 0) { toast.error('Sélectionnez des images'); return; }
    if (images.length > 10) { toast.error('Maximum 10 images'); return; }
    setSelectedImages(prev => [...prev, ...images]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const recorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm'
      });
      const chunks = [];
      let startTime = Date.now();
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        const duration = Math.floor((Date.now() - startTime) / 1000);
        if (duration < 1) { toast.error('Audio trop court'); stream.getTracks().forEach(t => t.stop()); return; }
        const blob = new Blob(chunks, { type: 'audio/mp4' });
        const file = new File([blob], `audio_${Date.now()}.mp4`, { type: 'audio/mp4' });
        await sendFile(file, 'audio');
        stream.getTracks().forEach(t => t.stop());
        setRecordingTime(0);
      };
      recorder.start(1000);
      setMediaRecorder(recorder);
      setRecording(true);
      recordingTimerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
    } catch (err) { toast.error('Microphone inaccessible'); }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };

  const searchUserByPhone = async () => {
    if (!searchPhone.trim()) return;
    setSearching(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/messagerie/search_user.php?telephone=${encodeURIComponent(searchPhone)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.utilisateur) {
        data.utilisateur.photo_profil = config.normalizeProfilePhoto(data.utilisateur.photo_profil);
        setFoundUser(data.utilisateur);
      } else { toast.error(data.message || 'Non trouvé'); }
    } catch { toast.error('Erreur'); }
    finally { setSearching(false); }
  };

  const startConversation = (user) => {
    setSelectedUser(user);
    setSelectedConversation(null);
    setMessages([]);
    setShowSearchModal(false);
    setSearchPhone('');
    setFoundUser(null);
    setSelectedImages([]);
    const existingConv = conversations.find(c => c.id_correspondant === user.id_utilisateur);
    if (existingConv) {
      setSelectedConversation(existingConv);
      fetchMessages(existingConv.id_conversation);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    setSelectedUser({
      id_utilisateur: conv.id_correspondant,
      nom: conv.correspondant_nom,
      prenom: conv.correspondant_prenom,
      photo_profil: conv.correspondant_photo,
      telephone: conv.correspondant_telephone,
      role: conv.correspondant_role
    });
    fetchMessages(conv.id_conversation);
    setSelectedImages([]);
  };

  const scrollToBottom = () => {
    setTimeout(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const openMediaModal = (url, type) => {
    setShowMediaModal({ url, type });
  };

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(() => {
      if (selectedConversation) fetchMessages(selectedConversation.id_conversation);
      fetchConversations();
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedConversation]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[550px]">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-sm font-medium text-slate-500">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-80px)] md:h-[calc(100vh-120px)] bg-slate-100 dark:bg-slate-950 rounded-3xl overflow-hidden shadow-xl border border-slate-200/50 dark:border-slate-800/50 relative antialiased text-slate-900 dark:text-slate-50">
      
      {/* 1. SIDEBAR */}
      <div className={`w-full lg:w-96 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col transition-all duration-300 ${selectedUser ? 'hidden lg:flex' : 'flex'}`}>
        <div className="p-5 flex items-center justify-between border-b border-slate-100 dark:border-slate-800">
          <h2 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Discussions</h2>
          <button
            onClick={() => { setShowSearchModal(true); setTimeout(() => searchInputRef.current?.focus(), 100); }}
            className="p-2.5 bg-slate-50 dark:bg-slate-800 text-emerald-600 dark:text-emerald-400 rounded-full hover:scale-105 active:scale-95 transition-all shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {conversations.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <p className="text-sm">Aucune discussion en cours</p>
            </div>
          ) : (
            conversations.map(conv => {
              const isActive = selectedConversation?.id_conversation === conv.id_conversation;
              return (
                <div
                  key={conv.id_conversation}
                  onClick={() => selectConversation(conv)}
                  className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-150 ${isActive ? 'bg-slate-100 dark:bg-slate-800' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`}
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center font-bold text-slate-600 dark:text-slate-300 overflow-hidden ring-1 ring-slate-100 dark:ring-slate-800">
                      {conv.correspondant_photo ? (
                        <img src={conv.correspondant_photo} alt="avatar" className="w-full h-full object-cover" />
                      ) : (
                        conv.correspondant_prenom?.charAt(0).toUpperCase()
                      )}
                    </div>
                    {conv.non_lus > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-emerald-500 text-white text-[10px] font-extrabold rounded-full flex items-center justify-center shadow-sm">
                        {conv.non_lus}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline mb-0.5">
                      <h3 className="font-semibold text-sm truncate">{conv.correspondant_prenom} {conv.correspondant_nom}</h3>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500">{formatDateTime(conv.dernier_message_date)}</span>
                    </div>
                    <p className={`text-xs truncate ${conv.non_lus > 0 ? 'text-slate-900 dark:text-slate-100 font-semibold' : 'text-slate-500'}`}>
                      {conv.dernier_message || 'Nouvelle discussion'}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 2. CHAT ZONE */}
      {selectedUser ? (
        <div className={`flex-1 flex flex-col w-full transition-all duration-300 bg-[#efeae2] dark:bg-[#0b141a] ${selectedUser ? 'flex' : 'hidden lg:flex'}`}>
          
          {/* Header */}
          <div className="p-3 bg-slate-200/90 dark:bg-[#202c33] backdrop-blur flex items-center gap-3 sticky top-0 z-20 shadow-sm border-b border-slate-300/30 dark:border-slate-700/30">
            <button
              onClick={() => { setSelectedUser(null); setSelectedConversation(null); }}
              className="lg:hidden p-1.5 text-slate-600 dark:text-slate-300 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="w-10 h-10 rounded-full bg-slate-300 dark:bg-slate-600 flex items-center justify-center font-bold overflow-hidden shadow-sm">
              {selectedUser.photo_profil ? (
                <img src={selectedUser.photo_profil} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                selectedUser.prenom?.charAt(0).toUpperCase()
              )}
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">{selectedUser.prenom} {selectedUser.nom}</h3>
              <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">en ligne</p>
            </div>
          </div>

          {/* Messages Corps (WhatsApp Styled) */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat opacity-[0.98]">
            {messages.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <span className="bg-white/80 dark:bg-[#182229] px-4 py-1.5 rounded-lg text-xs shadow-sm text-slate-500 dark:text-slate-400">Chiffrement de bout en bout</span>
              </div>
            ) : (
              messages.map(msg => {
                const isMine = msg.id_expediteur === currentUser.id_utilisateur;
                return (
                  <div key={msg.id_message} className={`flex ${isMine ? 'justify-end' : 'justify-start'} items-end gap-2`}>
                    {!isMine && (
                      <div className="h-8 w-8 rounded-full overflow-hidden bg-slate-300 flex items-center justify-center text-xs font-bold">
                        {(msg.photo_profil || msg.expediteur_photo) ? (
                          <img
                            src={config.normalizeProfilePhoto(msg.photo_profil || msg.expediteur_photo)}
                            alt="profil"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          msg.prenom?.charAt(0)?.toUpperCase() || 'U'
                        )}
                      </div>
                    )}
                    
                    {/* Bulle style WhatsApp */}
                    <div className={`max-w-[85%] md:max-w-[65%] rounded-xl px-3 py-1.5 shadow-sm relative group text-slate-900 dark:text-slate-100 ${
                      isMine 
                        ? 'bg-[#d9fdd3] dark:bg-[#005c4b] rounded-tr-none' 
                        : 'bg-white dark:bg-[#202c33] rounded-tl-none'
                    }`}>
                      
                      {/* Contenu textuel */}
                      {msg.type_message === 'texte' && (
                        <div className="text-[14px] break-words whitespace-pre-wrap pr-12 leading-normal" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatMessageContent(msg.contenu), { USE_PROFILES: { html: true } }) }} />
                      )}

                      {/* Média IMAGE (Format Carré Propre WhatsApp) */}
                      {msg.type_message === 'image' && msg.url_fichier && (
                        <div className="rounded-lg overflow-hidden my-0.5 max-w-sm border border-black/5 dark:border-white/5 bg-black/5 aspect-square max-h-64 sm:max-h-72">
                          <img 
                            src={msg.url_fichier} 
                            alt="Média" 
                            className="w-full h-full object-cover hover:opacity-95 transition cursor-pointer" 
                            onClick={() => openMediaModal(msg.url_fichier, 'image')} 
                          />
                        </div>
                      )}

                      {/* Média VIDÉO */}
                      {msg.type_message === 'video' && msg.url_fichier && (
                        <div className="rounded-lg overflow-hidden my-0.5 max-w-sm border border-black/5 bg-black aspect-video max-h-64">
                          <video controls className="w-full h-full object-contain"><source src={msg.url_fichier} type="video/mp4" /></video>
                        </div>
                      )}

                      {/* Média PDF */}
                      {msg.type_message === 'pdf' && msg.url_fichier && (
                        <a href={msg.url_fichier} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 my-0.5 bg-black/5 dark:bg-black/20 rounded-lg hover:bg-black/10 transition border border-black/5 dark:border-white/5 max-w-sm">
                          <div className="p-2 bg-red-500 text-white rounded-lg text-xs font-bold">PDF</div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium truncate">{msg.file_name || 'Document.pdf'}</p>
                          </div>
                        </a>
                      )}

                      {/* Média AUDIO */}
                      {msg.type_message === 'audio' && msg.url_fichier && (
                        <div className="flex items-center gap-2 p-1 my-0.5 bg-black/5 dark:bg-black/20 rounded-lg min-w-[200px]">
                          <audio src={msg.url_fichier} controls className="w-full h-8 accent-emerald-600 dark:accent-emerald-400" />
                        </div>
                      )}

                      {/* Heure et Statut (Flottant à droite façon WA) */}
                      <div className="absolute bottom-1 right-2 flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-300/60 select-none">
                        <span>{formatDateTime(msg.date_envoi)}</span>
                        {isMine && (
                          <span className={msg.est_lu ? "text-[#53bdeb]" : "text-slate-400"}>
                            {msg.est_lu ? '✓✓' : '✓'}
                          </span>
                        )}
                        <button
                          onClick={() => { setMessageToDelete(msg); setShowDeleteModal(true); }}
                          className="opacity-0 group-hover:opacity-100 text-red-500 dark:text-red-400 ml-1 transition-opacity duration-150"
                          title="Supprimer"
                        >
                          ✕
                        </button>
                      </div>

                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Saisie (WhatsApp Style) */}
          <div className="p-2 bg-slate-200/90 dark:bg-[#202c33] flex items-center gap-2 sticky bottom-0 z-20">
            <div className="flex items-center bg-white dark:bg-[#2a3942] rounded-full flex-1 px-3 py-1 shadow-sm border border-slate-300/20 dark:border-slate-700/20">
              
              <button onClick={() => fileInputRef.current?.click()} className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-emerald-500 transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageSelection} />

              <button onClick={() => document.getElementById('waDocInput')?.click()} className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-emerald-500 transition mr-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
              </button>
              <input type="file" id="waDocInput" accept="video/*,application/pdf,.pdf" className="hidden" onChange={(e) => {
                if (e.target.files?.[0]) {
                  const file = e.target.files[0];
                  if (file.type.startsWith('video/')) sendFile(file, 'video');
                  else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) sendFile(file, 'pdf');
                  else toast.error('Choisissez une vidéo ou un PDF.');
                }
                e.target.value = '';
              }} />

              <input
                ref={messageInputRef}
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') sendTextMessage(); }}
                placeholder={recording ? "Enregistrement..." : "Message"}
                disabled={recording}
                className="w-full py-1.5 bg-transparent text-sm focus:outline-none placeholder:text-slate-400 dark:text-slate-100"
              />
              
              <button onClick={() => setShowFormatMenu(!showFormatMenu)} className="p-1 text-xs font-serif font-bold text-slate-400 hover:text-emerald-500 transition ml-1">Aa</button>
            </div>

            <div className="flex-shrink-0">
              {newMessage.trim() || selectedImages.length > 0 ? (
                <button
                  onClick={selectedImages.length > 0 ? sendMultipleImages : sendTextMessage}
                  disabled={sending || sendingImages}
                  className="p-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-md flex items-center justify-center transition-transform active:scale-90"
                >
                  <svg className="w-4 h-4 transform rotate-90 fill-current" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" /></svg>
                </button>
              ) : (
                <button
                  onClick={recording ? stopRecording : startRecording}
                  className={`p-3 rounded-full shadow-md flex items-center justify-center transition-all ${recording ? 'bg-red-500 text-white animate-pulse' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                >
                  {recording ? <span className="text-[10px] font-bold">{recordingTime}s ⏹️</span> : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>}
                </button>
              )}
            </div>

            {/* Menu de formatage flottant */}
            {showFormatMenu && (
              <div ref={formatMenuRef} className="absolute bottom-16 left-4 bg-white dark:bg-slate-800 p-1.5 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 flex gap-1 z-30">
                <button onClick={() => applyFormat('**', '**')} className="px-2 py-1 text-xs font-bold rounded hover:bg-slate-100 dark:hover:bg-slate-700">G</button>
                <button onClick={() => applyFormat('*', '*')} className="px-2 py-1 text-xs italic rounded hover:bg-slate-100 dark:hover:bg-slate-700">I</button>
                <button onClick={() => applyFormat('~~', '~~')} className="px-2 py-1 text-xs line-through rounded hover:bg-slate-100 dark:hover:bg-slate-700">S</button>
                <button onClick={() => applyFormat('`', '`')} className="px-2 py-1 text-xs font-mono rounded hover:bg-slate-100 dark:hover:bg-slate-700">&lt;&gt;</button>
              </div>
            )}
          </div>

        </div>
      ) : (
        <div className="hidden lg:flex flex-1 flex-col items-center justify-center bg-slate-50 dark:bg-[#222e35] text-slate-400">
          <p className="text-sm">Sélectionnez une discussion pour commencer</p>
        </div>
      )}

      {/* ================= MODALS & VISIONNEUSE SÉCURISÉS MOBILE ================= */}

      {/* RECHERCHE MODAL */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 box-border">
          <div className="bg-white dark:bg-[#222e35] rounded-2xl p-5 w-full max-w-sm shadow-2xl box-border border border-slate-200/20">
            <h3 className="text-base font-bold mb-3">Nouvelle discussion</h3>
            <div className="flex flex-col gap-2 mb-4">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Numéro de téléphone..."
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-[#2a3942] border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none dark:text-white"
              />
              <button onClick={searchUserByPhone} disabled={searching} className="w-full py-2 bg-emerald-600 text-white font-medium rounded-xl text-sm transition">
                {searching ? 'Recherche...' : 'Rechercher'}
              </button>
            </div>
            {foundUser && (
              <div onClick={() => startConversation(foundUser)} className="flex items-center gap-3 p-2.5 bg-slate-50 dark:bg-[#2a3942] rounded-xl cursor-pointer mb-4">
                <div className="w-9 h-9 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center overflow-hidden">
                  {foundUser.photo_profil ? <img src={foundUser.photo_profil} alt="" className="w-full h-full object-cover" /> : foundUser.prenom?.charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold truncate">{foundUser.prenom} {foundUser.nom}</p>
                  <p className="text-[11px] text-slate-400 truncate">{foundUser.telephone}</p>
                </div>
              </div>
            )}
            <button onClick={() => { setShowSearchModal(false); setFoundUser(null); setSearchPhone(''); }} className="w-full py-2 text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition">Fermer</button>
          </div>
        </div>
      )}

      {/* SUPPRESSION MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 box-border">
          <div className="bg-white dark:bg-[#222e35] rounded-2xl p-5 w-full max-w-xs shadow-xl box-border">
            <h3 className="text-sm font-bold mb-1">Supprimer le message ?</h3>
            <p className="text-xs text-slate-400 mb-4">Le message sera effacé de votre historique.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowDeleteModal(false)} className="px-3 py-1.5 text-xs text-slate-500 rounded-lg">Annuler</button>
              <button onClick={deleteMessage} className="px-3 py-1.5 text-xs bg-red-500 text-white rounded-lg font-medium">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* VISIONNEUSE DE MÉDIA ANTI-DÉBORDEMENT MOBILE */}
      {showMediaModal && (
        <div 
          className="fixed inset-0 bg-black/95 z-[9999] flex flex-col items-center justify-center p-4 box-border touch-none select-none"
          onClick={() => setShowMediaModal(null)}
        >
          {/* Conteneur principal contraint en hauteur et largeur maximum écran */}
          <div className="w-full max-w-full max-h-full flex items-center justify-center relative box-border">
            {showMediaModal.type === 'image' && (
              <img 
                src={showMediaModal.url} 
                alt="HD" 
                className="max-w-full max-h-[82vh] md:max-h-[88vh] object-contain rounded shadow-2xl animate-in zoom-in-95 duration-150 pointer-events-auto"
                onClick={(e) => e.stopPropagation()} // Évite de fermer si on clique sur l'image elle-même
              />
            )}
          </div>
          <span className="text-white/60 text-[11px] mt-4 tracking-wide bg-white/10 px-3 py-1.5 rounded-full select-none">
            Fermer l'aperçu
          </span>
        </div>
      )}

    </div>
  );
}